import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { tenantAdminApi } from '../../utils/tenantAdminApi';
import { Button } from '../../components/ui/button';
import { Check, Crown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function BillingPage() {
  const [tenant, setTenant] = useState(null);
  const [plans, setPlans] = useState([]);
  const [tx, setTx] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState('');
  const [polling, setPolling] = useState(false);
  const [params, setParams] = useSearchParams();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [t, p, x] = await Promise.all([
        tenantAdminApi.tenant(), tenantAdminApi.plans(), tenantAdminApi.transactions(),
      ]);
      setTenant(t); setPlans(p); setTx(x);
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const sid = params.get('session_id');
    if (!sid) return;
    setPolling(true);
    let n = 0;
    const tick = async () => {
      n += 1;
      try {
        const s = await tenantAdminApi.checkoutStatus(sid);
        if (s.payment_status === 'paid') {
          toast.success('Payment received — plan upgraded');
          setPolling(false); setParams({}); refresh(); return;
        }
        if (s.status === 'expired') {
          toast.error('Checkout expired');
          setPolling(false); setParams({}); return;
        }
      } catch (_) {}
      if (n < 10) setTimeout(tick, 2000);
      else { setPolling(false); setParams({}); }
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const upgrade = async (plan_code) => {
    setPaying(plan_code);
    try {
      const res = await tenantAdminApi.upgrade({ plan_code, origin_url: window.location.origin });
      if (res.free) {
        toast.success('Plan switched to Free');
        refresh();
      } else if (res.url) {
        window.location.assign(res.url);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to start upgrade');
    }
    setPaying('');
  };

  const currentCode = tenant?.plan_code;

  return (
    <div className="p-8 max-w-6xl">
      <h1
        className="text-2xl font-bold text-zinc-900"
        style={{ fontFamily: 'Manrope' }}
        data-testid="ta-billing-title"
      >
        Billing &amp; Plan
      </h1>
      <p className="text-sm text-zinc-500 mt-1">
        Upgrade or downgrade your subscription. Changes apply immediately after payment.
      </p>

      {polling && (
        <div className="mt-4 bg-amber-50 border border-amber-200 text-amber-900 px-4 py-2 rounded-lg text-sm flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Verifying payment…
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
        {loading ? (
          <p className="text-sm text-zinc-500 col-span-3">Loading…</p>
        ) : plans.map((p) => {
          const isCurrent = p.code === currentCode;
          return (
            <div
              key={p.id}
              className={`bg-white border rounded-xl p-5 flex flex-col ${isCurrent ? 'ring-2 ring-blue-500' : ''}`}
              data-testid={`ta-plan-card-${p.code}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500">{p.code}</div>
                  <div className="text-xl font-bold text-zinc-900 mt-0.5">{p.name}</div>
                </div>
                {isCurrent && (
                  <span className="text-[11px] font-semibold px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1">
                    <Crown className="h-3 w-3" /> Current
                  </span>
                )}
              </div>
              <div className="mt-3 text-3xl font-bold text-zinc-900">
                ${p.price_monthly}
                <span className="text-sm font-normal text-zinc-500"> /mo</span>
              </div>
              <ul className="mt-4 space-y-1.5 text-sm text-zinc-700 flex-1">
                {(p.features || []).map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => upgrade(p.code)}
                disabled={isCurrent || paying === p.code}
                className={`mt-5 ${isCurrent ? '' : 'bg-blue-700 hover:bg-blue-800'}`}
                variant={isCurrent ? 'outline' : 'default'}
                data-testid={`ta-plan-select-${p.code}`}
              >
                {paying === p.code ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {isCurrent ? 'Current plan' : p.price_monthly > 0 ? 'Upgrade' : 'Switch to Free'}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="mt-8">
        <h3 className="font-semibold text-zinc-900 mb-3">Payment history</h3>
        <div className="bg-white border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b text-left text-zinc-600">
              <tr>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Plan</th>
                <th className="px-4 py-3 font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {tx.length === 0 ? (
                <tr><td colSpan="4" className="px-4 py-6 text-center text-zinc-500">No transactions yet.</td></tr>
              ) : tx.map((r) => (
                <tr key={r.id} className="border-b last:border-0" data-testid={`ta-tx-${r.session_id}`}>
                  <td className="px-4 py-3 text-zinc-600">{new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' })}</td>
                  <td className="px-4 py-3 text-zinc-900">{r.plan_code}</td>
                  <td className="px-4 py-3 font-medium">${(r.amount || 0).toFixed(2)} {r.currency?.toUpperCase()}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${
                      r.payment_status === 'paid'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : r.payment_status === 'pending'
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-zinc-50 text-zinc-700 border-zinc-200'
                    }`}>
                      {r.payment_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
